import React, { useEffect, useRef, useState } from 'react';

/** Detecta la preferencia del sistema de reducir movimiento. */
export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(mq.matches);
    update();
    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, []);
  return reduced;
}

interface CountUpProps {
  to: number;
  active: boolean;
  duration?: number;
  className?: string;
}

/**
 * Número que cuenta hacia arriba cuando su slide se activa.
 * Reinicia a 0 al desactivarse (para reproducir de nuevo si se vuelve).
 * Si el usuario pide reducir movimiento, muestra el valor final directamente.
 */
export const CountUp: React.FC<CountUpProps> = ({ to, active, duration = 1600, className }) => {
  const reduced = useReducedMotion();
  const [val, setVal] = useState(0);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (!active) {
      setVal(0);
      return;
    }
    if (reduced || duration <= 0) {
      setVal(to);
      return;
    }
    const start = performance.now();
    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(to * eased));
      if (p < 1) rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [active, to, duration, reduced]);

  return <span className={className}>{val}</span>;
};
